import { COACHES, ACADEMY } from "../lib/academy-data";
import SectionHeader from "../components/SectionHeader";
import { Star, Trophy, Brain, Activity, Users } from "lucide-react";

const STAFF_GROUPS = [
  {
    icon: Trophy,
    title: "צוות מאמני כדורסל",
    text: "צוות מאמנים בכיר ממחלקת הנוער של הפועל מטה יהודה.",
  },
  {
    icon: Activity,
    title: "מאמן יכולות גופניות",
    text: "אחראי על אימוני אתלטיקה וחדר כושר — פיתוח כוח, מהירות וזריזות.",
  },
  {
    icon: Brain,
    title: "ליווי מנטלי",
    text: `${ACADEMY.mentalCoach} ופסיכולוג ספורט — סדנאות, שיחות וליווי פרטני.`,
  },
  {
    icon: Users,
    title: "מדריכים חברתיים",
    text: "מדריכים ייעודיים לספורטאים — לטיפוח כישורים חברתיים ותמיכה אישית.",
  },
];

export default function Coaches() {
  return (
    <div data-testid="coaches-page">
      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-[#0A0A0A]">
        <img
          src="https://images.pexels.com/photos/9711756/pexels-photo-9711756.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
          alt="מאמן"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#0A0A0A] to-[#0A0A0A]/70" />
        <div className="container mx-auto px-6 md:px-10 relative">
          <p className="font-heading text-xs md:text-sm font-bold tracking-[0.3em] text-[#DC2626] uppercase mb-4">
            הצוות המקצועי
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight leading-[0.9] max-w-4xl">
            מאחורי כל שחקן —
            <br />
            <span className="text-[#DC2626]">צוות מנצח.</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-neutral-300 mt-6 leading-relaxed max-w-3xl">
            צוות מקצועי שלם שמלווה את הספורטאים שלנו — מאמני כדורסל בכירים, מאמני כושר, יועצים מנטליים, ומדריכים חברתיים.
          </p>
        </div>
      </section>

      {/* Featured: Yoram Menachem */}
      <section className="py-20 md:py-24 bg-[#050505]">
        <div className="container mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center bg-[#171717] border border-white/10 p-8 md:p-12">
            <div className="lg:col-span-5 relative">
              <div className="absolute -top-3 -right-3 w-full h-full border-2 border-[#DC2626]" />
              <img
                src={COACHES[0].image}
                alt={COACHES[0].name}
                className="relative w-full h-[460px] object-cover"
              />
            </div>
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-[#DC2626] text-white font-heading font-bold text-xs uppercase tracking-widest px-3 py-1.5 mb-4">
                <Star size={14} strokeWidth={3} />
                {COACHES[0].badge}
              </div>
              <h2 className="font-heading text-5xl md:text-6xl font-black text-white uppercase tracking-tight leading-[0.95]">
                {COACHES[0].name}
              </h2>
              <p className="font-heading text-xl text-[#DC2626] font-bold mt-2">
                {COACHES[0].role}
              </p>
              <p className="font-body text-base md:text-lg text-neutral-300 mt-6 leading-relaxed">
                {COACHES[0].description}
              </p>
              <p className="font-body text-base md:text-lg text-neutral-400 mt-4 leading-relaxed">
                האקדמיה זוכה לליווי מקצועי צמוד שמבטיח שכל שחקן ושחקנית מקבלים את הכלים הטובים
                ביותר להתפתחות — בקבוצה ובאופן אישי.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Other coaches */}
      <section className="py-20 md:py-24 bg-[#0A0A0A]">
        <div className="container mx-auto px-6 md:px-10">
          <SectionHeader
            kicker="הנהגת האקדמיה"
            title="המנהל והצוות"
            testId="coaches-team-header"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {COACHES.slice(1).map((coach, i) => (
              <div
                key={coach.name}
                className="group bg-[#171717] border border-white/10 hover:border-[#DC2626]/50 transition-colors overflow-hidden"
                data-testid={`coach-card-${i}`}
              >
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={coach.image}
                    alt={coach.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#171717] to-transparent" />
                </div>
                <div className="p-7">
                  <h3 className="font-heading text-2xl md:text-3xl font-black text-white uppercase">
                    {coach.name}
                  </h3>
                  <p className="font-heading text-sm text-[#DC2626] font-bold tracking-wider uppercase mt-1">
                    {coach.role}
                  </p>
                  <p className="font-body text-sm md:text-base text-neutral-400 mt-4 leading-relaxed">
                    {coach.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Staff groups */}
      <section className="py-20 md:py-24 bg-[#050505]">
        <div className="container mx-auto px-6 md:px-10">
          <SectionHeader
            kicker="הצוות המורחב"
            title="כל אחד במקומו"
            description="מעבר למאמנים הראשיים — האקדמיה מעמידה צוות מקצועי שלם שמלווה את הספורטאים בכל היבט."
            testId="staff-groups-header"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {STAFF_GROUPS.map((g, i) => (
              <div
                key={g.title}
                className="bg-[#171717] border border-white/10 hover:border-[#DC2626]/50 transition-colors p-7 flex gap-5"
                data-testid={`staff-group-${i}`}
              >
                <div className="w-12 h-12 inline-flex items-center justify-center bg-[#DC2626] text-white flex-shrink-0">
                  <g.icon size={22} strokeWidth={2.5} />
                </div>
                <div>
                  <h4 className="font-heading text-xl font-bold text-white mb-2">{g.title}</h4>
                  <p className="font-body text-sm md:text-base text-neutral-400 leading-relaxed">
                    {g.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
